<script setup lang="ts">
  import { ref, computed } from "vue";
  import { Dialog, Text, Button, IconStopLarge, Chip, Badge } from "@ui-kit";
  import { useOrderStatus, type Product, ResponseCode } from "@src/api";
  import { ProductMiniCard } from "@src/components";
  import { useWidgetsStore } from "@src/store";
  import { getOrderReasonList } from "@src/dictionary";
  import { useStateActionList, useOrders } from "./state";

  const { isOpen } = defineProps<{ isOpen: boolean }>();
  const emit = defineEmits<{ (e: "close"): void; (e: "complete"): void }>();

  const { mutateAsync: orderStatus, isPending: isOrderStatusPending } = useOrderStatus();

  const state = useOrders();
  const action_list_state = useStateActionList();
  const widgets_store = useWidgetsStore();

  const reason = ref<string | null>(null);

  const not_collected_items = computed(() => {
    const _not_collected_items: Array<{ seria: Product; sizes: string[] }> = [];

    // Получаем список товаров, которые не были собраны и подготавливаем размеры (пример: 42, 44, 46)
    Object.values(state.value.selected.serias).forEach((itemSeria) => {
      const sizesOfFilteredGoods = state.value.selected.goodsBySeria[itemSeria.external_id].reduce<string[]>(
        (acc, goodId) => {
          const good = state.value.selected.mapGoods[goodId];

          if (good.quantity > action_list_state.getCountActions({ itemId: good.item.item_id, status: "done" })) {
            acc.push(good.item.size);
          }

          return acc;
        },
        [],
      );

      if (sizesOfFilteredGoods.length > 0) {
        _not_collected_items.push({
          seria: itemSeria,
          sizes: sizesOfFilteredGoods,
        });
      }
    });

    return _not_collected_items;
  });

  const handleComplete = async () => {
    if (!widgets_store.order_card.order_id || (not_collected_items.value.length > 0 && !reason.value)) return;

    const result = await orderStatus({
      order_id: widgets_store.order_card.order_id,
      status: "done",
      reason: reason.value,
    });

    if (result.code === ResponseCode.OK) {
      emit("complete");
    }
  };
</script>

<template>
  <Dialog :isOpen="isOpen" @close="emit('close')" data-testid="order-complete-modal">
    <div class="order-complete-modal">
      <div class="content">
        <IconStopLarge style="flex-shrink: 0" />
        <div class="info">
          <Text font="body-bold-18">
            {{ `${$t("Завершить сборку")}?` }}
          </Text>
          <Text v-if="not_collected_items.length > 0" font="body-regular-14">
            {{ `${$t("Некоторые товары не отсканированы")}. ${$t("Для завершения выберите причину")}.` }}
          </Text>
        </div>
        <div v-if="not_collected_items.length > 0" class="not-scanned-items">
          <ProductMiniCard
            v-for="item in not_collected_items"
            :key="item.seria.item_id"
            :image="item.seria.image_url"
            :name="item.seria.title"
            :article="item.seria.article"
          >
            <template #additional-info>
              <div class="additional-info">
                <Badge v-for="size in item.sizes" :key="size" :label="size" variant="error" size="xs" />
              </div>
            </template>
          </ProductMiniCard>
        </div>
        <div class="reasons">
          <Chip
            v-for="[value, label] in getOrderReasonList()"
            :key="value"
            :label="label"
            :selected="reason === value"
            @click="reason = value"
            :data-testid="`reason-chip-${value}`"
          />
        </div>
      </div>
      <div class="footer">
        <Button
          :disabled="not_collected_items.length > 0 && !reason"
          @click="handleComplete"
          :isLoading="isOrderStatusPending"
          data-testid="complete-btn"
        >
          {{ $t("Да, завершить") }}
        </Button>
        <Button variant="secondary" @click="emit('close')" data-testid="cancel-btn">
          {{ $t("Отменить") }}
        </Button>
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
  .order-complete-modal {
    width: 328px;
    height: 520px;
    display: flex;
    flex-direction: column;
    padding: 20px;
    gap: 24px;

    .content {
      display: flex;
      flex-direction: column;
      gap: 16px;
      align-items: center;
      overflow-y: auto;
    }

    .info {
      display: flex;
      flex-direction: column;
      gap: 4px;
      align-items: center;
      text-align: center;
    }

    .not-scanned-items {
      display: flex;
      flex-direction: column;
      gap: 12px;
      width: 100%;
      overflow-y: auto;

      & :deep(.item .item__image) {
        width: 52px;
        height: 64px;
      }

      & :deep(.item .info) {
        gap: 0;
      }

      & :deep(.item .info .text:first-of-type) {
        margin-bottom: 2px;
      }

      & :deep(.item .content) {
        align-items: flex-start;
      }
    }

    .additional-info {
      display: flex;
      gap: 4px;
      flex-wrap: wrap;
      margin-top: 4px;
    }

    .reasons {
      display: flex;
      gap: 8px;
    }

    .footer {
      display: flex;
      flex-direction: column;
      gap: 8px;

      & :deep(.button) {
        width: 100%;
      }
    }
  }
</style>
